import Phaser from "phaser";
import type { ProfileRedactionConfig } from "../types.js";
import { applyProfileRedaction } from "./profile-redaction.js";

/** Block size for retro pixelation (matches terminal input pixel grid). */
const PROFILE_PIXEL_SIZE = 4;

function getSourceSize(source: TexImageSource): { width: number; height: number } {
    if (source instanceof HTMLImageElement) {
        return { width: source.naturalWidth, height: source.naturalHeight };
    }

    if (source instanceof HTMLCanvasElement || source instanceof ImageBitmap) {
        return { width: source.width, height: source.height };
    }

    if (source instanceof HTMLVideoElement) {
        return { width: source.videoWidth, height: source.videoHeight };
    }

    return { width: 0, height: 0 };
}

export function applyTerminalGreenFilter(
    scene: Phaser.Scene,
    sourceKey: string,
    filteredKey: string,
    options?: { pixelSize?: number; redaction?: ProfileRedactionConfig },
): string {
    if (scene.textures.exists(filteredKey)) {
        return filteredKey;
    }

    const texture = scene.textures.get(sourceKey);
    const source = texture.getSourceImage();
    if (!(source instanceof HTMLImageElement || source instanceof HTMLCanvasElement || source instanceof ImageBitmap)) {
        return sourceKey;
    }

    const { width, height } = getSourceSize(source);
    if (width <= 0 || height <= 0) {
        return sourceKey;
    }

    const pixelSize = options?.pixelSize ?? PROFILE_PIXEL_SIZE;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
        return sourceKey;
    }

    if (pixelSize <= 1) {
        ctx.drawImage(source, 0, 0, width, height);
    } else {
        const pixelW = Math.max(1, Math.floor(width / pixelSize));
        const pixelH = Math.max(1, Math.floor(height / pixelSize));

        const small = document.createElement("canvas");
        small.width = pixelW;
        small.height = pixelH;

        const smallCtx = small.getContext("2d");
        if (!smallCtx) {
            return sourceKey;
        }

        smallCtx.imageSmoothingEnabled = false;
        smallCtx.drawImage(source, 0, 0, pixelW, pixelH);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(small, 0, 0, width, height);
    }

    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        const r = data[i]!;
        const g = data[i + 1]!;
        const b = data[i + 2]!;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;

        data[i] = luma * 0.12;
        data[i + 1] = luma * 0.92;
        data[i + 2] = luma * 0.16;
    }

    if (options?.redaction) {
        applyProfileRedaction(imageData, options.redaction);
    }

    ctx.putImageData(imageData, 0, 0);

    scene.textures.addCanvas(filteredKey, canvas);
    if (pixelSize > 1) {
        scene.textures.get(filteredKey).setFilter(Phaser.Textures.FilterMode.NEAREST);
    }
    return filteredKey;
}
